<script lang="ts">
  import { onMount } from 'svelte';
  import gsap from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import { shots } from './assets';
  import PhoneFrame from './lib/PhoneFrame.svelte';

  const gameUrl = 'https://play.2k48.online';
  const showcaseFrames = [
    {
      id: 'duelo',
      kicker: '01 · DUELO EN VIVO',
      title: 'Cada fusión hace daño.',
      copy: 'El tablero sigue siendo 2048. La diferencia es que ahora cada gran ficha carga un golpe contra alguien real.',
      image: shots.gameplay,
      alt: 'Partida versus de 2K48 Battle con dos jugadores y tablero 4 por 4',
    },
    {
      id: 'poder',
      kicker: '02 · PODER 1024',
      title: 'La ficha se convierte en ataque.',
      copy: 'Encadena fusiones, llena el combo y dispara un impacto capaz de voltear la ronda en un solo instante.',
      image: shots.power,
      alt: 'Ataque de poder 1024 atravesando el tablero de 2K48 Battle',
    },
    {
      id: 'ready',
      kicker: '03 · MISMO RELOJ',
      title: 'Los dos entran al mismo segundo.',
      copy: 'READY, 3, 2, 1, GO. La cuenta se sincroniza para que la pelea empiece igual de justa para ambos.',
      image: shots.countdown,
      alt: 'Cuenta regresiva sincronizada antes de una partida de 2K48 Battle',
    },
  ] as const;

  let root: HTMLElement;
  let showcaseImage: HTMLImageElement;
  let selectedFrame = $state(0);
  const activeFrame = $derived(showcaseFrames[selectedFrame] ?? showcaseFrames[0]);
  let reduceMotion = false;

  function selectFrame(index: number): void {
    if (index === selectedFrame) return;
    if (reduceMotion || !showcaseImage) {
      selectedFrame = index;
      return;
    }

    gsap.killTweensOf(showcaseImage);
    gsap.to(showcaseImage, {
      autoAlpha: 0,
      y: 18,
      scale: 0.985,
      duration: 0.18,
      ease: 'power2.in',
      onComplete: () => {
        selectedFrame = index;
        requestAnimationFrame(() => {
          gsap.fromTo(
            showcaseImage,
            { autoAlpha: 0, y: -18, scale: 0.985 },
            { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: 'power3.out' },
          );
        });
      },
    });
  }

  onMount(() => {
    gsap.registerPlugin(ScrollTrigger);
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const mm = gsap.matchMedia();
    const context = gsap.context(() => {
      const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });
      intro
        .from('.site-nav', { y: -24, autoAlpha: 0, duration: 0.55 })
        .from('.hero__eyebrow', { y: 20, autoAlpha: 0, duration: 0.5 }, 0.12)
        .from('.hero__word', { yPercent: 115, rotate: 3, duration: 0.78, stagger: 0.09 }, 0.2)
        .from(
          '.hero__lede, .hero__actions, .hero__proof',
          {
            y: 22,
            autoAlpha: 0,
            duration: 0.55,
            stagger: 0.1,
          },
          0.46,
        )
        .from('.hero-stage__back', { x: 80, rotate: 11, autoAlpha: 0, duration: 0.9 }, 0.24)
        .from('.hero-stage__front', { y: 80, rotate: -6, autoAlpha: 0, duration: 0.9 }, 0.34)
        .from(
          '.hero-stage__signal, .hero-stage__badge',
          {
            scale: 0.72,
            autoAlpha: 0,
            duration: 0.45,
            stagger: 0.08,
          },
          0.7,
        );

      gsap.to('[data-float]', {
        y: 'random(-16, 16, 2)',
        rotate: 'random(-7, 7, 1)',
        duration: 'random(2.6, 4.2, 0.2)',
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        stagger: 0.18,
      });

      gsap.to('.ticker__track', {
        xPercent: -50,
        duration: 24,
        ease: 'none',
        repeat: -1,
      });

      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((element) => {
        gsap.from(element, {
          y: 54,
          autoAlpha: 0,
          immediateRender: false,
          duration: 0.75,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 88%',
            once: true,
          },
        });
      });

      gsap.utils.toArray<HTMLElement>('[data-stagger]').forEach((group) => {
        gsap.from(group.children, {
          y: 36,
          autoAlpha: 0,
          immediateRender: false,
          duration: 0.6,
          stagger: 0.09,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: group,
            start: 'top 84%',
            once: true,
          },
        });
      });

      gsap.to('.finale__tile', {
        rotate: 3,
        y: -22,
        ease: 'none',
        scrollTrigger: {
          trigger: '.finale',
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.7,
        },
      });

      mm.add('(min-width: 900px)', () => {
        gsap.to('.hero-stage', {
          yPercent: 11,
          ease: 'none',
          scrollTrigger: {
            trigger: '.hero',
            start: 'top top',
            end: 'bottom top',
            scrub: 0.8,
          },
        });

        const stage = root.querySelector<HTMLElement>('.hero-stage');
        if (!stage) return;
        const xTo = gsap.quickTo(stage, 'x', { duration: 0.7, ease: 'power3.out' });
        const yTo = gsap.quickTo(stage, 'y', { duration: 0.7, ease: 'power3.out' });
        const onPointerMove = (event: PointerEvent) => {
          xTo((event.clientX / window.innerWidth - 0.5) * 20);
          yTo((event.clientY / window.innerHeight - 0.5) * 14);
        };
        window.addEventListener('pointermove', onPointerMove, { passive: true });
        return () => window.removeEventListener('pointermove', onPointerMove);
      });
    }, root);

    return () => {
      mm.revert();
      context.revert();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  });
</script>

<svelte:head>
  <meta
    name="description"
    content="2K48 Battle es el puzzle arcade competitivo donde cada fusión golpea a tu rival. Juega gratis en web."
  />
  <meta property="og:title" content="2K48 Battle — El 2048 que aprendió a pelear" />
  <meta
    property="og:description"
    content="Fusiona. Carga tu combo. Golpea antes de que el rival te deje en K.O."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://2k48.online" />
  <meta property="og:image" content="https://2k48.online/og-2k48.png" />
  <meta property="og:image:width" content="1425" />
  <meta property="og:image:height" content="1000" />
  <meta name="twitter:card" content="summary_large_image" />
</svelte:head>

<div class="site" bind:this={root}>
  <a class="skip-link" href="#main">Saltar al contenido</a>

  <header class="site-nav">
    <a class="brand" href="#top" aria-label="2K48 Battle, inicio">
      <span class="brand__mark">2K48</span>
      <span class="brand__battle">BATTLE</span>
    </a>
    <nav aria-label="Navegación principal">
      <a href="#concepto">El giro</a>
      <a href="#gameplay">Gameplay</a>
      <a href="#modos">Modos</a>
    </nav>
    <a class="nav-play" href={gameUrl}>JUGAR <span aria-hidden="true">↗</span></a>
  </header>

  <main id="main">
    <section class="hero" id="top" aria-labelledby="hero-title">
      <div class="hero__glow hero__glow--cyan" aria-hidden="true"></div>
      <div class="hero__glow hero__glow--mag" aria-hidden="true"></div>
      <div class="hero__grid" aria-hidden="true"></div>

      <div class="hero__copy">
        <p class="hero__eyebrow"><span></span> PUZZLE ARCADE · PVP EN TIEMPO REAL</p>
        <h1 id="hero-title">
          <span class="hero__line"><span class="hero__word">FUSIONA.</span></span>
          <span class="hero__line"><span class="hero__word hero__word--mag">ATACA.</span></span>
          <span class="hero__line"
            ><span class="hero__word hero__word--yellow">SOBREVIVE.</span></span
          >
        </h1>
        <p class="hero__lede">
          El puzzle que conoces, convertido en una pelea. Construye fichas, carga el combo y
          <strong>golpea a tu rival antes de que él te deje en K.O.</strong>
        </p>
        <div class="hero__actions">
          <a class="button button--primary" href={gameUrl}>
            <span class="button__flare" aria-hidden="true"></span>
            JUGAR GRATIS
            <span aria-hidden="true">▶</span>
          </a>
          <a class="button button--quiet" href="#gameplay">VER EL COMBATE <span>↓</span></a>
        </div>
        <div class="hero__proof" aria-label="Características principales">
          <span><b>60</b> SEGUNDOS</span>
          <i></i>
          <span><b>1V1</b> EN VIVO</span>
          <i></i>
          <span><b>0</b> PAY TO WIN</span>
        </div>
      </div>

      <div class="hero-stage" aria-label="Capturas reales de una partida">
        <div class="hero-stage__halo" aria-hidden="true"></div>
        <PhoneFrame
          class="hero-stage__back"
          src={shots.power}
          alt="Ataque de poder 1024 durante una partida"
          loading="eager"
        />
        <PhoneFrame
          class="hero-stage__front"
          src={shots.gameplay}
          alt="Pantalla de una partida versus de 2K48 Battle"
          loading="eager"
        />

        <span class="hero-stage__signal" data-float>
          <small>IMPACTO</small>
          <strong>-28</strong>
        </span>
        <span class="hero-stage__badge" data-float>
          <i></i>
          PARTIDA EN VIVO
        </span>
        <span class="float-tile float-tile--2" data-float aria-hidden="true">2</span>
        <span class="float-tile float-tile--8" data-float aria-hidden="true">8</span>
        <span class="float-tile float-tile--64" data-float aria-hidden="true">64</span>
      </div>

      <p class="hero__scroll" aria-hidden="true"><span></span> DESLIZA PARA ENTRAR A LA ARENA</p>
    </section>

    <div class="ticker" aria-hidden="true">
      <div class="ticker__track">
        <span>MERGE</span><i>◆</i><span>STRIKE</span><i>◆</i><span>CLIMB</span><i>◆</i><span
          >OUTPLAY</span
        ><i>◆</i><span>MERGE</span><i>◆</i><span>STRIKE</span><i>◆</i><span>CLIMB</span><i>◆</i
        ><span>OUTPLAY</span><i>◆</i>
        <span>MERGE</span><i>◆</i><span>STRIKE</span><i>◆</i><span>CLIMB</span><i>◆</i><span
          >OUTPLAY</span
        ><i>◆</i><span>MERGE</span><i>◆</i><span>STRIKE</span><i>◆</i><span>CLIMB</span><i>◆</i
        ><span>OUTPLAY</span><i>◆</i>
      </div>
    </div>

    <section class="concept section" id="concepto">
      <div class="section__eyebrow" data-reveal>EL GIRO</div>
      <div class="concept__heading" data-reveal>
        <h2>2048 aprendió<br />a <em>pelear.</em></h2>
        <p>
          Aquí no esperas a que el tablero se llene. Cada decisión también está sucediendo en la
          pantalla de tu rival.
        </p>
      </div>

      <div class="battle-formula" data-stagger>
        <article class="formula-card">
          <span class="formula-card__number">01</span>
          <div class="formula-card__visual formula-card__visual--merge" aria-hidden="true">
            <i>64</i><b>+</b><i>64</i>
          </div>
          <h3>FUSIONA</h3>
          <p>Juega rápido, protege tus espacios y construye una ficha que importe.</p>
        </article>
        <article class="formula-card formula-card--charged">
          <span class="formula-card__number">02</span>
          <div class="formula-card__visual formula-card__visual--charge" aria-hidden="true">
            <span><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i></span>
            <b>COMBO x8</b>
          </div>
          <h3>CARGA</h3>
          <p>Encadena jugadas. Un buen ritmo convierte puntos en presión real.</p>
        </article>
        <article class="formula-card formula-card--impact">
          <span class="formula-card__number">03</span>
          <div class="formula-card__visual formula-card__visual--impact" aria-hidden="true">
            <i>1024</i><span>⚡</span><b>-28</b>
          </div>
          <h3>GOLPEA</h3>
          <p>Rompe su barra de vida y sigue jugando mientras la arena explota.</p>
        </article>
      </div>
    </section>

    <section class="showcase section" id="gameplay">
      <div class="showcase__visual" data-reveal>
        <div class="showcase__orbit" aria-hidden="true"></div>
        <div class="showcase__phone">
          <span class="showcase__live"><i></i> REAL GAMEPLAY</span>
          <div class="showcase__screen">
            <img
              bind:this={showcaseImage}
              src={activeFrame.image}
              alt={activeFrame.alt}
              loading="lazy"
              decoding="async"
            />
          </div>
          <span class="showcase__meta">CAPTURA REAL · SIN CONCEPT ART</span>
        </div>
      </div>

      <div class="showcase__copy">
        <div class="section__eyebrow" data-reveal>ASÍ SE SIENTE</div>
        <h2 data-reveal>Un segundo estás pensando.<br /><em>Al siguiente, atacando.</em></h2>
        <div class="showcase__tabs" data-stagger role="tablist" aria-label="Estados de una partida">
          {#each showcaseFrames as frame, index (frame.id)}
            <button
              class:active={selectedFrame === index}
              role="tab"
              aria-selected={selectedFrame === index}
              aria-controls="showcase-panel"
              onclick={() => selectFrame(index)}
            >
              <span>{frame.kicker}</span>
              <strong>{frame.title}</strong>
              <small>{frame.copy}</small>
            </button>
          {/each}
        </div>
        <div id="showcase-panel" role="tabpanel" class="sr-only">
          {activeFrame.title}
          {activeFrame.copy}
        </div>
      </div>
    </section>

    <section class="world section" id="modos">
      <div class="world__heading" data-reveal>
        <div>
          <div class="section__eyebrow">MÁS QUE UNA RONDA</div>
          <h2>Entra por el puzzle.<br /><em>Quédate por la rivalidad.</em></h2>
        </div>
        <p>
          Duelo rápido, salas privadas, progreso y tablas distintas para quienes dominan el versus y
          quienes persiguen la puntuación perfecta.
        </p>
      </div>

      <div class="bento" data-stagger>
        <article class="bento-card bento-card--lobby">
          <div class="bento-card__copy">
            <span>PARTIDA PRIVADA</span>
            <h3>Tu sala.<br />Tus reglas.</h3>
            <p>Comparte un código y reta a quien tengas enfrente o al otro lado del mundo.</p>
          </div>
          <div class="bento-phone bento-phone--lobby" aria-hidden="true">
            <img src={shots.lobby} alt="" loading="lazy" />
          </div>
          <span class="room-code" aria-hidden="true">#7K2Q</span>
        </article>

        <article class="bento-card bento-card--stat">
          <span class="bento-card__label">UNA RONDA</span>
          <strong>60</strong>
          <small>SEGUNDOS</small>
          <div class="stat-ring" aria-hidden="true"><i></i></div>
        </article>

        <article class="bento-card bento-card--modes">
          <span class="bento-card__label">ELIGE TU ARENA</span>
          <ul>
            <li><i>01</i><b>VS ONLINE</b><span>RANKED</span></li>
            <li><i>02</i><b>SALA PRIVADA</b><span>CÓDIGO</span></li>
            <li><i>03</i><b>CONTRA BOT</b><span>TRAINING</span></li>
            <li><i>04</i><b>SOLO</b><span>RÉCORD</span></li>
          </ul>
        </article>

        <article class="bento-card bento-card--rank">
          <div class="bento-card__copy">
            <span>RANKING GLOBAL</span>
            <h3>Tu nombre no sube solo.</h3>
            <p>ELO para los duelos. High scores para el tablero. Dos formas de demostrarlo.</p>
          </div>
          <div class="bento-phone bento-phone--rank" aria-hidden="true">
            <img src={shots.ranking} alt="" loading="lazy" />
          </div>
        </article>
      </div>
    </section>

    <section class="anywhere section">
      <div class="anywhere__panel" data-reveal>
        <div class="anywhere__copy">
          <div class="section__eyebrow">MISMA PELEA. DONDE SEA.</div>
          <h2>Entra desde web.<br /><em>Sigue en tu bolsillo.</em></h2>
          <p>
            El mismo juego, la misma cuenta y el mismo rival. Teclado, mouse o swipe: el tablero
            responde a ti.
          </p>
          <a class="text-link" href={gameUrl}>ABRIR 2K48 BATTLE <span>↗</span></a>
        </div>
        <div class="device-stack" aria-label="2K48 Battle funciona en web y móvil">
          <div class="device-stack__desktop">
            <span></span>
            <img src={shots.menu} alt="Menú principal de 2K48 Battle" loading="lazy" />
          </div>
          <div class="device-stack__mobile">
            <img src={shots.gameplay} alt="Partida de 2K48 Battle en móvil" loading="lazy" />
          </div>
        </div>
      </div>
    </section>

    <section class="finale">
      <div class="finale__noise" aria-hidden="true"></div>
      <div class="finale__tile" aria-hidden="true"><span>2048</span></div>
      <div class="finale__copy" data-reveal>
        <p>LA ARENA YA ESTÁ ABIERTA</p>
        <h2>¿Vas a sumar puntos<br />o vas a <em>ganar?</em></h2>
        <a class="button button--primary button--large" href={gameUrl}>
          ENTRAR A LA BATALLA <span>▶</span>
        </a>
        <small>GRATIS · JUEGA EN EL NAVEGADOR · SIN DESCARGA</small>
      </div>
    </section>
  </main>

  <footer class="site-footer">
    <a class="brand" href="#top" aria-label="Volver arriba">
      <span class="brand__mark">2K48</span>
      <span class="brand__battle">BATTLE</span>
    </a>
    <p>EL PUZZLE ARCADE QUE GOLPEA DE VUELTA.</p>
    <nav aria-label="Enlaces legales">
      <a href="mailto:soporte@2k48.online">SOPORTE</a>
      <a href="/privacidad">PRIVACIDAD</a>
      <a href={gameUrl}>JUGAR</a>
    </nav>
    <small>© 2026 2K48 BATTLE · JUEGO INDEPENDIENTE</small>
  </footer>
</div>
